import slugify from 'slugify'

import type { Bill, Session } from '@/payload-types'
import type { Payload, PayloadRequest } from 'payload'

/**
 * URL slug for a bill from BillTrack50 `stateBillID` (scope/session disambiguate in the path).
 */
export function billSlugFromStateBillID(stateBillID: string): string {
  const trimmed = stateBillID.trim()
  if (trimmed.length === 0) {
    throw new Error('stateBillID is required to build a bill slug')
  }

  return slugify(trimmed, { lower: true, strict: true })
}

/**
 * Converts BillTrack50 `stateCode` to the legislative tracker URL scope segment.
 */
export function billStateCodeToScopeSlug(stateCode: string | null | undefined): string {
  if (!stateCode) {
    return 'us'
  }

  return stateCode.toUpperCase() === 'US' ? 'us' : stateCode.toLowerCase()
}

/**
 * Reads the session slug when `session` is already a populated document.
 * Relationship IDs return null; use {@link resolveBillSessionSlug} to look those up.
 */
export function getBillSessionSlug(bill: Bill): string | null {
  const session = bill.session

  if (typeof session === 'object' && session !== null && 'slug' in session) {
    const slug = (session as Session).slug
    return typeof slug === 'string' && slug.length > 0 ? slug : null
  }

  return null
}

/**
 * Resolves a bill's session slug from a populated session, or by loading the
 * `sessions` document when the relationship is stored as an ID.
 */
export async function resolveBillSessionSlug(
  bill: Bill,
  payload: Payload,
  req?: Partial<PayloadRequest>,
): Promise<string | null> {
  const populated = getBillSessionSlug(bill)
  if (populated) {
    return populated
  }

  const sessionId = bill.session
  if (typeof sessionId !== 'number') {
    return null
  }

  const session = await payload.findByID({
    collection: 'sessions',
    id: sessionId,
    depth: 0,
    disableErrors: true,
    overrideAccess: true,
    select: {
      slug: true,
    },
    ...(req ? { req } : {}),
  })

  if (!session || typeof session.slug !== 'string' || session.slug.length === 0) {
    return null
  }

  return session.slug
}

/**
 * Builds `/legislative-tracker/bills/{scope}/{session}/{slug}`, or null when a segment is missing.
 */
export function legislativeTrackerBillPath(
  scopeSlug: string,
  sessionSlug: string | null,
  slug: string | null | undefined,
): string | null {
  if (!scopeSlug || !sessionSlug || !slug) {
    return null
  }

  return `/legislative-tracker/bills/${scopeSlug}/${sessionSlug}/${slug}`
}

/**
 * Builds the public legislative tracker URL for a bill, or null when required fields are missing.
 * Requires a populated `session`. Callers that only have a relationship ID should resolve it first.
 */
export function getBillLegislativeTrackerUrl(bill: Bill): string | null {
  return legislativeTrackerBillPath(
    billStateCodeToScopeSlug(bill.bt50?.stateCode),
    getBillSessionSlug(bill),
    bill.slug,
  )
}

/**
 * Returns the display bill identifier (e.g. HB 1234).
 */
export function getBillIdentifier(bill: Bill): string {
  const stateBillID = bill.bt50?.stateBillID
  if (typeof stateBillID === 'string' && stateBillID.length > 0) {
    return stateBillID
  }

  return bill.title ?? ''
}
